"use client";

import { c as _c } from "react/compiler-runtime";
import * as MapLibreGL from "maplibre-gl";
import { createContext, useContext, useEffect, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import { cn } from "#/lib/utils";
import { useMap } from "./map";
import { jsx as _jsx } from "react/jsx-runtime";
const MarkerContext = /*#__PURE__*/createContext(null);
function useMarkerContext() {
  const context = useContext(MarkerContext);
  if (!context) {
    throw new Error("Marker components must be used within MapMarker");
  }
  return context;
}
function MapMarker({
  longitude,
  latitude,
  children,
  onClick,
  onMouseEnter,
  onMouseLeave,
  onDragStart,
  onDrag,
  onDragEnd,
  draggable = false,
  ...markerOptions
}) {
  const {
    map
  } = useMap();
  const callbacksRef = useRef({
    onClick,
    onMouseEnter,
    onMouseLeave,
    onDragStart,
    onDrag,
    onDragEnd
  });
  // Latest-callback ref synced during render on purpose (read only from marker event handlers).
  // eslint-disable-next-line react-hooks/refs
  callbacksRef.current = {
    onClick,
    onMouseEnter,
    onMouseLeave,
    onDragStart,
    onDrag,
    onDragEnd
  };
  const marker = useMemo(() => {
    const markerInstance = new MapLibreGL.Marker({
      ...markerOptions,
      element: document.createElement("div"),
      draggable
    }).setLngLat([longitude, latitude]);

    // The handlers below only read callbacksRef when an event fires, never
    // during render; the rule cannot see that through addEventListener/on.
    /* eslint-disable react-hooks/refs */
    const handleClick = e => callbacksRef.current.onClick?.(e);
    const handleMouseEnter = e => callbacksRef.current.onMouseEnter?.(e);
    const handleMouseLeave = e => callbacksRef.current.onMouseLeave?.(e);
    markerInstance.getElement()?.addEventListener("click", handleClick);
    markerInstance.getElement()?.addEventListener("mouseenter", handleMouseEnter);
    markerInstance.getElement()?.addEventListener("mouseleave", handleMouseLeave);
    const handleDragStart = () => {
      const lngLat = markerInstance.getLngLat();
      callbacksRef.current.onDragStart?.({
        lng: lngLat.lng,
        lat: lngLat.lat
      });
    };
    const handleDrag = () => {
      const lngLat = markerInstance.getLngLat();
      callbacksRef.current.onDrag?.({
        lng: lngLat.lng,
        lat: lngLat.lat
      });
    };
    const handleDragEnd = () => {
      const lngLat = markerInstance.getLngLat();
      callbacksRef.current.onDragEnd?.({
        lng: lngLat.lng,
        lat: lngLat.lat
      });
    };
    markerInstance.on("dragstart", handleDragStart);
    markerInstance.on("drag", handleDrag);
    markerInstance.on("dragend", handleDragEnd);
    /* eslint-enable react-hooks/refs */

    return markerInstance;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (!map) return;
    marker.addTo(map);
    return () => {
      marker.remove();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map]);
  if (marker.getLngLat().lng !== longitude || marker.getLngLat().lat !== latitude) {
    marker.setLngLat([longitude, latitude]);
  }
  if (marker.isDraggable() !== draggable) {
    marker.setDraggable(draggable);
  }
  const currentOffset = marker.getOffset();
  const newOffset = markerOptions.offset ?? [0, 0];
  const [newOffsetX, newOffsetY] = Array.isArray(newOffset) ? newOffset : [newOffset.x, newOffset.y];
  if (currentOffset.x !== newOffsetX || currentOffset.y !== newOffsetY) {
    marker.setOffset(newOffset);
  }
  if (marker.getRotation() !== markerOptions.rotation) {
    marker.setRotation(markerOptions.rotation ?? 0);
  }
  if (marker.getRotationAlignment() !== markerOptions.rotationAlignment) {
    marker.setRotationAlignment(markerOptions.rotationAlignment ?? "auto");
  }
  if (marker.getPitchAlignment() !== markerOptions.pitchAlignment) {
    marker.setPitchAlignment(markerOptions.pitchAlignment ?? "auto");
  }
  return /*#__PURE__*/_jsx(MarkerContext.Provider, {
    value: {
      marker,
      map
    },
    children: children
  });
}
function MarkerContent(t0) {
  const $ = _c(10);
  const {
    children,
    className
  } = t0;
  const {
    marker
  } = useMarkerContext();
  let t1;
  if ($[0] !== className) {
    t1 = cn("relative cursor-pointer", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== children) {
    t2 = children || /*#__PURE__*/_jsx(DefaultMarkerIcon, {});
    $[2] = children;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = /*#__PURE__*/_jsx("div", {
      className: t1,
      children: t2
    });
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== marker || $[8] !== t3) {
    t4 = /*#__PURE__*/createPortal(t3, marker.getElement());
    $[7] = marker;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
}
function DefaultMarkerIcon() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = /*#__PURE__*/_jsx("div", {
      className: "relative h-4 w-4 rounded-full border-2 border-white bg-blue-500 shadow-lg"
    });
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
function MarkerLabel(t0) {
  const $ = _c(6);
  const {
    children,
    className,
    position: t1
  } = t0;
  const position = t1 === undefined ? "top" : t1;
  let t2;
  if ($[0] !== className || $[1] !== position) {
    const positionClasses = {
      top: "bottom-full mb-1",
      bottom: "top-full mt-1"
    };
    t2 = cn("absolute left-1/2 -translate-x-1/2 whitespace-nowrap", "text-foreground text-[10px] font-medium", positionClasses[position], className);
    $[0] = className;
    $[1] = position;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== children || $[4] !== t2) {
    t3 = /*#__PURE__*/_jsx("div", {
      className: t2,
      children: children
    });
    $[3] = children;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  return t3;
}
export { MapMarker, MarkerContent, MarkerContext, MarkerLabel, useMarkerContext };